/**
 * AddDncRuleModal — unified Internal DNC entry surface.
 *
 * One modal with three tabs:
 *   1. Type / Paste — free-form textarea, live per-line classification
 *   2. Contacts    — cross-tenant contact search, multi-select
 *   3. CSV Import  — drag/drop or pick a phone-only CSV, classify + preview
 *
 * Each tab stages a set of *valid* additions then commits them to the parent
 * draft rules via onAdd. No backend writes happen here — the parent's existing
 * Save flow persists everything.
 */
import { useState, useMemo, useCallback, useEffect, useRef, type DragEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Badge,
  Checkbox,
  cn,
} from '@respectly/ui';
import {
  Keyboard,
  Users,
  Upload,
  Hash,
  Phone,
  Globe2,
  AlertCircle,
  CheckCircle2,
  Search,
  Loader2,
  X,
  FileText,
  Trash2,
} from 'lucide-react';
import {
  classifyRule,
  splitEntries,
  type ClassifyResult,
  type RuleKind,
} from './dncRuleParser';
import {
  useInternalDncStore,
  type ContactSearchResult,
  type InternalDncRules,
} from '../../stores/useInternalDncStore';

// ============================================
// Types
// ============================================

type TabKey = 'type' | 'contacts' | 'csv';

export interface StagedAddition {
  kind: RuleKind;
  value: string; // canonical (no trailing *)
  displayValue: string; // for chips (with * for prefixes)
  source?: { type: 'manual' | 'contact' | 'csv'; label?: string };
}

interface AddDncRuleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing: InternalDncRules;
  onAdd: (additions: StagedAddition[]) => void;
}

// ============================================
// Helpers
// ============================================

const KIND_META: Record<
  RuleKind,
  { label: string; icon: typeof Hash; tone: string; ring: string }
> = {
  exactNumber: {
    label: 'Exact',
    icon: Phone,
    tone: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    ring: 'ring-emerald-200 dark:ring-emerald-800',
  },
  prefix: {
    label: 'Wildcard',
    icon: Globe2,
    tone: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    ring: 'ring-amber-200 dark:ring-amber-800',
  },
  areaCode: {
    label: 'Area Code',
    icon: Hash,
    tone: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    ring: 'ring-blue-200 dark:ring-blue-800',
  },
};

function isDuplicateOfExisting(
  result: { kind: RuleKind; value: string },
  existing: InternalDncRules,
): boolean {
  if (result.kind === 'exactNumber') return existing.exactNumbers.includes(result.value);
  if (result.kind === 'prefix') return existing.prefixes.includes(result.value);
  return existing.areaCodes.includes(result.value);
}

function KindBadge({ kind }: { kind: RuleKind }) {
  const meta = KIND_META[kind];
  const Icon = meta.icon;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide',
        meta.tone,
      )}
    >
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
}

// ============================================
// Main Component
// ============================================

export function AddDncRuleModal({ open, onOpenChange, existing, onAdd }: AddDncRuleModalProps) {
  const [tab, setTab] = useState<TabKey>('type');

  // Reset to first tab whenever the modal opens
  useEffect(() => {
    if (open) setTab('type');
  }, [open]);

  const handleCommit = useCallback(
    (additions: StagedAddition[]) => {
      if (additions.length === 0) return;
      onAdd(additions);
      onOpenChange(false);
    },
    [onAdd, onOpenChange],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl p-0 gap-0 overflow-hidden"
        showCloseButton={false}
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200 dark:border-slate-700 space-y-1">
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle className="text-lg font-semibold flex items-center gap-2">
                Add to Internal DNC
              </DialogTitle>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Add suppression rules by typing numbers, picking contacts, or importing a CSV.
              </p>
            </div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Tab strip */}
          <div className="flex items-center gap-1 -mb-px pt-3">
            <TabButton active={tab === 'type'} onClick={() => setTab('type')} icon={Keyboard}>
              Type or Paste
            </TabButton>
            <TabButton active={tab === 'contacts'} onClick={() => setTab('contacts')} icon={Users}>
              Pick from Contacts
            </TabButton>
            <TabButton active={tab === 'csv'} onClick={() => setTab('csv')} icon={Upload}>
              Import CSV
            </TabButton>
          </div>
        </DialogHeader>

        <div className="bg-slate-50/40 dark:bg-slate-900/40">
          {tab === 'type' && (
            <TypeTab existing={existing} onCommit={handleCommit} />
          )}
          {tab === 'contacts' && (
            <ContactsTab existing={existing} onCommit={handleCommit} />
          )}
          {tab === 'csv' && (
            <CsvTab existing={existing} onCommit={handleCommit} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================
// Tab Button
// ============================================

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  icon: typeof Keyboard;
  children: React.ReactNode;
}

function TabButton({ active, onClick, icon: Icon, children }: TabButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-t-md border-b-2 transition-colors',
        active
          ? 'border-rose-500 text-rose-600 dark:text-rose-400 bg-white dark:bg-slate-900'
          : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200',
      )}
    >
      <Icon className="w-3.5 h-3.5" />
      {children}
    </button>
  );
}

// ============================================
// Tab A — Type / Paste
// ============================================

interface TabProps {
  existing: InternalDncRules;
  onCommit: (additions: StagedAddition[]) => void;
}

function TypeTab({ existing, onCommit }: TabProps) {
  const [text, setText] = useState('');

  const parsed = useMemo(() => {
    const entries = splitEntries(text);
    const seen = new Set<string>();
    return entries.map((raw) => {
      const result = classifyRule(raw);
      let dupKind: 'self' | 'existing' | null = null;
      if (result.kind !== 'invalid') {
        const key = `${result.kind}:${result.value}`;
        if (seen.has(key)) dupKind = 'self';
        else if (isDuplicateOfExisting(result, existing)) dupKind = 'existing';
        seen.add(key);
      }
      return { raw, result, dupKind };
    });
  }, [text, existing]);

  const valid = parsed.filter((p) => p.result.kind !== 'invalid' && p.dupKind === null);
  const invalidCount = parsed.filter((p) => p.result.kind === 'invalid').length;
  const dupCount = parsed.filter((p) => p.dupKind !== null).length;

  const handleAdd = () => {
    const additions: StagedAddition[] = [];
    for (const p of parsed) {
      if (p.result.kind === 'invalid' || p.dupKind !== null) continue;
      additions.push({
        kind: p.result.kind,
        value: p.result.value,
        displayValue: p.result.displayValue,
        source: { type: 'manual' },
      });
    }
    onCommit(additions);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 divide-x divide-slate-200 dark:divide-slate-700">
      <div className="p-5 space-y-3">
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">
          Numbers or Patterns
        </label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={'+14155551234\n+1404555*\n555'}
          rows={10}
          className={cn(
            'w-full rounded-lg border border-slate-200 dark:border-slate-700',
            'bg-white dark:bg-slate-900 px-3 py-2 text-sm font-mono',
            'focus:outline-none focus:ring-2 focus:ring-rose-300/40 focus:border-rose-400',
            'placeholder:text-slate-300 dark:placeholder:text-slate-600',
          )}
        />
        <div className="text-[11px] text-slate-500 dark:text-slate-400 space-y-1">
          <p>One entry per line (or separate with commas).</p>
          <p>
            <span className="font-mono text-emerald-600 dark:text-emerald-400">+14155551234</span> exact number ·{' '}
            <span className="font-mono text-amber-600 dark:text-amber-400">+1404555*</span> wildcard ·{' '}
            <span className="font-mono text-blue-600 dark:text-blue-400">555</span> US area code
          </p>
        </div>
      </div>

      <div className="p-5 space-y-3 min-h-[280px] max-h-[420px] overflow-auto">
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">
            Preview {parsed.length > 0 && <span className="font-normal text-slate-400">· {parsed.length}</span>}
          </label>
          {parsed.length > 0 && (
            <div className="flex items-center gap-2 text-[11px]">
              <span className="text-emerald-600 dark:text-emerald-400">{valid.length} ok</span>
              {dupCount > 0 && <span className="text-amber-600 dark:text-amber-400">{dupCount} dup</span>}
              {invalidCount > 0 && <span className="text-red-600 dark:text-red-400">{invalidCount} invalid</span>}
            </div>
          )}
        </div>

        {parsed.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center text-slate-400 dark:text-slate-500">
            <FileText className="w-8 h-8 mb-2 opacity-50" />
            <p className="text-xs">Entries will appear here as you type.</p>
          </div>
        ) : (
          <div className="space-y-1">
            {parsed.map((p, i) => (
              <PreviewRow key={`${i}-${p.raw}`} raw={p.raw} result={p.result} dupKind={p.dupKind} />
            ))}
          </div>
        )}
      </div>

      <div className="col-span-full flex items-center justify-between px-5 py-3 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700">
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {valid.length > 0
            ? `${valid.length} new ${valid.length === 1 ? 'rule' : 'rules'} ready to add`
            : 'Add at least one valid entry to continue'}
        </p>
        <Button
          size="sm"
          onClick={handleAdd}
          disabled={valid.length === 0}
          className="bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white border-0"
        >
          Add {valid.length > 0 ? `${valid.length} ` : ''}to DNC
        </Button>
      </div>
    </div>
  );
}

function PreviewRow({
  raw,
  result,
  dupKind,
}: {
  raw: string;
  result: ClassifyResult;
  dupKind: 'self' | 'existing' | null;
}) {
  if (result.kind === 'invalid') {
    return (
      <div className="flex items-start gap-2 px-2 py-1.5 rounded bg-red-50/60 dark:bg-red-950/30 border border-red-100 dark:border-red-900/40">
        <AlertCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-mono text-red-700 dark:text-red-300 truncate">{raw}</p>
          <p className="text-[11px] text-red-600 dark:text-red-400 mt-0.5">{result.reason}</p>
        </div>
      </div>
    );
  }

  if (dupKind) {
    return (
      <div className="flex items-center gap-2 px-2 py-1.5 rounded bg-amber-50/60 dark:bg-amber-950/30 border border-amber-100 dark:border-amber-900/40">
        <AlertCircle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
        <span className="text-xs font-mono text-slate-700 dark:text-slate-300 flex-1 truncate">
          {result.displayValue}
        </span>
        <KindBadge kind={result.kind} />
        <span className="text-[10px] text-amber-700 dark:text-amber-400">
          {dupKind === 'existing' ? 'already in list' : 'duplicate'}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded bg-white dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700">
      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
      <span className="text-xs font-mono text-slate-800 dark:text-slate-200 flex-1 truncate">
        {result.displayValue}
      </span>
      <KindBadge kind={result.kind} />
    </div>
  );
}

// ============================================
// Tab B — Contacts (cross-tenant)
// ============================================

function ContactsTab({ existing, onCommit }: TabProps) {
  const searchContacts = useInternalDncStore((s) => s.searchContacts);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ContactSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selected, setSelected] = useState<Map<string, ContactSearchResult>>(new Map());
  const requestIdRef = useRef(0);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setIsSearching(false);
      return;
    }
    const myRequestId = ++requestIdRef.current;
    setIsSearching(true);
    const t = setTimeout(async () => {
      const r = await searchContacts(trimmed);
      if (requestIdRef.current === myRequestId) {
        setResults(r);
        setIsSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query, searchContacts]);

  const toggle = (c: ContactSearchResult) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(c.id)) next.delete(c.id);
      else next.set(c.id, c);
      return next;
    });
  };

  const handleAdd = () => {
    const additions: StagedAddition[] = [];
    for (const c of selected.values()) {
      if (!c.phone) continue;
      const result = classifyRule(c.phone);
      if (result.kind === 'invalid') continue;
      if (isDuplicateOfExisting(result, existing)) continue;
      const fullName = `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || 'Contact';
      const tenant = c.tenant?.name ?? 'Unknown tenant';
      additions.push({
        kind: result.kind,
        value: result.value,
        displayValue: result.displayValue,
        source: { type: 'contact', label: `${fullName} (${tenant})` },
      });
    }
    onCommit(additions);
  };

  const selectedCount = selected.size;
  const hint = query.trim().length < 2 ? 'Type at least 2 characters to search' : null;

  return (
    <div className="flex flex-col">
      <div className="p-5 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, phone, or email — across all tenants"
            className="pl-9 h-9"
            autoFocus
          />
          {isSearching && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
          )}
        </div>
      </div>

      <div className="min-h-[280px] max-h-[420px] overflow-auto px-1 py-2">
        {hint ? (
          <div className="flex flex-col items-center justify-center h-[260px] text-center text-slate-400 dark:text-slate-500">
            <Users className="w-8 h-8 mb-2 opacity-50" />
            <p className="text-xs">{hint}</p>
            <p className="text-[11px] mt-1 opacity-70">Searches all contacts across every tenant.</p>
          </div>
        ) : results.length === 0 && !isSearching ? (
          <div className="flex flex-col items-center justify-center h-[260px] text-center text-slate-400 dark:text-slate-500">
            <Search className="w-8 h-8 mb-2 opacity-50" />
            <p className="text-xs">No contacts match "{query.trim()}"</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {results.map((c) => {
              const isSelected = selected.has(c.id);
              const initials = `${(c.firstName ?? ' ')[0] ?? ''}${(c.lastName ?? ' ')[0] ?? ''}`
                .trim()
                .toUpperCase() || '?';
              const fullName = `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || 'Unnamed';
              const phoneInList = c.phone ? isDuplicateOfExisting(
                { kind: 'exactNumber', value: c.phone },
                existing,
              ) : false;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => c.phone && !phoneInList && toggle(c)}
                    disabled={!c.phone || phoneInList}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                      isSelected
                        ? 'bg-rose-50 dark:bg-rose-950/30'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-800/40',
                      (!c.phone || phoneInList) && 'opacity-50 cursor-not-allowed',
                    )}
                  >
                    <Checkbox
                      checked={isSelected}
                      disabled={!c.phone || phoneInList}
                      onCheckedChange={() => c.phone && !phoneInList && toggle(c)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center text-[11px] font-semibold text-slate-700 dark:text-slate-200 flex-shrink-0">
                      {initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-slate-900 dark:text-white truncate">
                          {fullName}
                        </span>
                        <span className="font-mono text-xs text-slate-500 dark:text-slate-400">
                          {c.phone ?? '—'}
                        </span>
                        {phoneInList && (
                          <span className="text-[10px] text-amber-600 dark:text-amber-400">
                            already in DNC
                          </span>
                        )}
                      </div>
                      {c.tenant && (
                        <div className="mt-0.5">
                          <Badge variant="secondary" className="text-[10px] font-normal">
                            {c.tenant.name}
                          </Badge>
                        </div>
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="flex items-center justify-between px-5 py-3 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700">
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {selectedCount > 0
            ? `${selectedCount} ${selectedCount === 1 ? 'contact' : 'contacts'} selected`
            : 'Select contacts to add their phone numbers'}
        </p>
        <Button
          size="sm"
          onClick={handleAdd}
          disabled={selectedCount === 0}
          className="bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white border-0"
        >
          Add {selectedCount > 0 ? `${selectedCount} ` : ''}to DNC
        </Button>
      </div>
    </div>
  );
}

// ============================================
// Tab C — CSV Import (native, no extra deps)
// ============================================

interface CsvRow {
  rowNum: number;
  raw: string;
  result: ClassifyResult;
  dupKind: 'self' | 'existing' | null;
}

function parsePhoneCsv(text: string): string[] {
  // Strip BOM, then split into lines. Each line we take the first column only
  // (split by , or ; or tab). Skip a leading "phone"/"number" header line.
  const cleaned = text.replace(/^\uFEFF/, '');
  const lines = cleaned.split(/\r?\n/);
  if (lines.length === 0) return [];

  const firstCol = (line: string): string => {
    // simple first-column extractor; tolerant of quoted values
    const m = line.match(/^\s*"([^"]*)"|^\s*([^,;\t]*)/);
    return ((m?.[1] ?? m?.[2]) ?? '').trim();
  };

  const firstColZero = firstCol(lines[0] ?? '').toLowerCase();
  const startIdx = ['phone', 'number', 'phone number', 'phonenumber', 'msisdn'].includes(firstColZero)
    ? 1
    : 0;

  const out: string[] = [];
  for (let i = startIdx; i < lines.length; i++) {
    const v = firstCol(lines[i] ?? '');
    if (v) out.push(v);
  }
  return out;
}

function CsvTab({ existing, onCommit }: TabProps) {
  const [fileName, setFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<CsvRow[]>([]);
  const [filter, setFilter] = useState<'all' | 'valid' | 'errors'>('all');
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const ingestFile = useCallback(
    async (file: File) => {
      setError(null);
      if (!/\.(csv|txt)$/i.test(file.name)) {
        setError('Please drop a .csv or .txt file.');
        return;
      }
      const text = await file.text();
      const entries = parsePhoneCsv(text);
      if (entries.length === 0) {
        setError('No phone-like rows found in the file.');
        setFileName(file.name);
        setRows([]);
        return;
      }
      const seen = new Set<string>();
      const next: CsvRow[] = entries.map((raw, idx) => {
        const result = classifyRule(raw);
        let dupKind: 'self' | 'existing' | null = null;
        if (result.kind !== 'invalid') {
          const key = `${result.kind}:${result.value}`;
          if (seen.has(key)) dupKind = 'self';
          else if (isDuplicateOfExisting(result, existing)) dupKind = 'existing';
          seen.add(key);
        }
        return { rowNum: idx + 1, raw, result, dupKind };
      });
      setFileName(file.name);
      setRows(next);
    },
    [existing],
  );

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void ingestFile(file);
  };

  const handlePick = () => fileInputRef.current?.click();

  const reset = () => {
    setFileName(null);
    setRows([]);
    setFilter('all');
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const validRows = rows.filter((r) => r.result.kind !== 'invalid' && r.dupKind === null);
  const errorRows = rows.filter((r) => r.result.kind === 'invalid');
  const dupRows = rows.filter((r) => r.dupKind !== null);

  const visibleRows =
    filter === 'valid' ? validRows : filter === 'errors' ? [...errorRows, ...dupRows] : rows;

  const handleAdd = () => {
    const additions: StagedAddition[] = validRows.map((r) => ({
      kind: (r.result as { kind: RuleKind }).kind,
      value: (r.result as { value: string }).value,
      displayValue: (r.result as { displayValue: string }).displayValue,
      source: { type: 'csv', label: fileName ?? undefined },
    }));
    onCommit(additions);
  };

  return (
    <div className="flex flex-col">
      <div className="p-5">
        {!fileName ? (
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={handlePick}
            className={cn(
              'border-2 border-dashed rounded-xl px-6 py-10 text-center cursor-pointer transition-colors',
              dragOver
                ? 'border-rose-400 bg-rose-50 dark:bg-rose-950/20'
                : 'border-slate-300 dark:border-slate-700 hover:border-rose-300 dark:hover:border-rose-700 bg-white dark:bg-slate-900',
            )}
          >
            <div className="w-12 h-12 rounded-xl bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center mx-auto mb-3">
              <Upload className="w-6 h-6 text-rose-500" />
            </div>
            <p className="text-sm font-semibold text-slate-900 dark:text-white">
              Drop a CSV here, or click to browse
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Single column of phone numbers, area codes, or wildcards (e.g.{' '}
              <span className="font-mono text-amber-600 dark:text-amber-400">+1404555*</span>).
              First-row <span className="font-mono">phone</span> header is detected and skipped.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt,text/csv,text/plain"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void ingestFile(f);
              }}
            />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-slate-500 flex-shrink-0" />
                <span className="text-sm font-medium text-slate-900 dark:text-white truncate">
                  {fileName}
                </span>
                <span className="text-xs text-slate-400 tabular-nums">· {rows.length} rows</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={reset}
                className="text-xs text-slate-500 hover:text-rose-600"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                Discard
              </Button>
            </div>

            <div className="flex items-center gap-1.5">
              <FilterPill
                active={filter === 'all'}
                onClick={() => setFilter('all')}
                count={rows.length}
                label="All"
                tone="slate"
              />
              <FilterPill
                active={filter === 'valid'}
                onClick={() => setFilter('valid')}
                count={validRows.length}
                label="Valid"
                tone="emerald"
              />
              <FilterPill
                active={filter === 'errors'}
                onClick={() => setFilter('errors')}
                count={errorRows.length + dupRows.length}
                label="Skipped"
                tone="amber"
              />
            </div>
          </div>
        )}

        {error && (
          <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/40">
            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}
      </div>

      {fileName && rows.length > 0 && (
        <div className="min-h-[200px] max-h-[320px] overflow-auto px-5 pb-4">
          <div className="space-y-1">
            <AnimatePresence initial={false}>
              {visibleRows.map((r) => (
                <motion.div
                  key={`${r.rowNum}-${r.raw}`}
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <PreviewRow raw={r.raw} result={r.result} dupKind={r.dupKind} />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between px-5 py-3 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700">
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {validRows.length > 0
            ? `${validRows.length} valid ${validRows.length === 1 ? 'rule' : 'rules'} · ${
                errorRows.length + dupRows.length
              } skipped`
            : fileName
            ? 'No valid rules in this file'
            : 'Drop a CSV to preview rules'}
        </p>
        <Button
          size="sm"
          onClick={handleAdd}
          disabled={validRows.length === 0}
          className="bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white border-0"
        >
          Import {validRows.length > 0 ? `${validRows.length} ` : ''}
          {validRows.length === 1 ? 'rule' : 'rules'}
        </Button>
      </div>
    </div>
  );
}

function FilterPill({
  active,
  onClick,
  count,
  label,
  tone,
}: {
  active: boolean;
  onClick: () => void;
  count: number;
  label: string;
  tone: 'slate' | 'emerald' | 'amber';
}) {
  const toneActive: Record<typeof tone, string> = {
    slate: 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900',
    emerald: 'bg-emerald-600 text-white',
    amber: 'bg-amber-500 text-white',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors',
        active
          ? toneActive[tone]
          : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700',
      )}
    >
      {label}
      <span className="tabular-nums opacity-80">{count}</span>
    </button>
  );
}
